// Servicio unificado de IA que enruta consultas a Gemini o MiniMax según la configuración activa
// y registra el consumo y costo de tokens

import { getAiConfig, logAiQuery, calculateTokenCost } from './aiConfigService';
import {
  analyzeTranscriptionMiniMax,
  analyzeTranscriptionWithAttendanceMiniMax,
  analyzeCsvOnlyMiniMax,
} from './minimaxService';
import {
  analyzeTranscription as analyzeTranscriptionGemini,
  analyzeTranscriptionWithAttendance as analyzeTranscriptionWithAttendanceGemini,
  analyzeCsvOnly as analyzeCsvOnlyGemini,
} from './geminiService';

/**
 * Procesa una transcripción con el proveedor de IA activo
 */
export const unifiedAnalyzeTranscription = async (transcription, fileName = 'transcripción') => {
  const config = getAiConfig();
  const startTime = performance.now();

  try {
    if (config.provider === 'minimax') {
      const res = await analyzeTranscriptionMiniMax(transcription, config);
      const durationMs = Math.round(performance.now() - startTime);
      const cost = calculateTokenCost(res.usage.inputTokens, res.usage.outputTokens, config);

      const logEntry = logAiQuery({
        queryType: 'Transcripción',
        fileName,
        provider: 'MiniMax',
        model: config.minimaxModel,
        inputTokens: res.usage.inputTokens,
        outputTokens: res.usage.outputTokens,
        durationMs,
        success: true,
        thinkingSnippet: res.thinking ? res.thinking.substring(0, 300) : null,
      });

      return {
        data: res.data,
        metrics: {
          provider: 'MiniMax',
          model: config.minimaxModel,
          inputTokens: res.usage.inputTokens,
          outputTokens: res.usage.outputTokens,
          totalTokens: res.usage.totalTokens,
          costUsd: cost.totalUsd,
          costCop: cost.totalCop,
          formattedUsd: cost.formattedUsd,
          formattedCop: cost.formattedCop,
          durationMs,
          thinking: res.thinking,
          logId: logEntry?.id,
        },
      };
    } else {
      // Gemini
      const data = await analyzeTranscriptionGemini(transcription);
      const durationMs = Math.round(performance.now() - startTime);
      const estInputTokens = Math.round(transcription.length / 4);
      const estOutputTokens = 600;

      logAiQuery({
        queryType: 'Transcripción',
        fileName,
        provider: 'Gemini',
        model: 'gemini-3.1-flash-lite',
        inputTokens: estInputTokens,
        outputTokens: estOutputTokens,
        durationMs,
        success: true,
      });

      return {
        data,
        metrics: {
          provider: 'Gemini',
          model: 'gemini-3.1-flash-lite',
          inputTokens: estInputTokens,
          outputTokens: estOutputTokens,
          totalTokens: estInputTokens + estOutputTokens,
          costUsd: 0,
          costCop: 0,
          formattedUsd: '$0.00',
          formattedCop: '$0 COP',
          durationMs,
          thinking: null,
        },
      };
    }
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    logAiQuery({
      queryType: 'Transcripción',
      fileName,
      provider: config.provider === 'minimax' ? 'MiniMax' : 'Gemini',
      model: config.provider === 'minimax' ? config.minimaxModel : 'gemini-3.1-flash-lite',
      inputTokens: 0,
      outputTokens: 0,
      durationMs,
      success: false,
      error: error.message,
    });
    throw error;
  }
};

/**
 * Procesa únicamente archivo CSV con el proveedor de IA activo
 */
export const unifiedAnalyzeCsvOnly = async (csvContent, fileName = 'asistencia.csv') => {
  const config = getAiConfig();
  const startTime = performance.now();

  try {
    if (config.provider === 'minimax') {
      const res = await analyzeCsvOnlyMiniMax(csvContent, config);
      const durationMs = Math.round(performance.now() - startTime);
      const cost = calculateTokenCost(res.usage.inputTokens, res.usage.outputTokens, config);

      const logEntry = logAiQuery({
        queryType: 'Lista CSV',
        fileName,
        provider: 'MiniMax',
        model: config.minimaxModel,
        inputTokens: res.usage.inputTokens,
        outputTokens: res.usage.outputTokens,
        durationMs,
        success: true,
      });

      return {
        data: res.data,
        metrics: {
          provider: 'MiniMax',
          model: config.minimaxModel,
          inputTokens: res.usage.inputTokens,
          outputTokens: res.usage.outputTokens,
          totalTokens: res.usage.totalTokens,
          costUsd: cost.totalUsd,
          costCop: cost.totalCop,
          formattedUsd: cost.formattedUsd,
          formattedCop: cost.formattedCop,
          durationMs,
          thinking: res.thinking,
          logId: logEntry?.id,
        },
      };
    } else {
      const data = await analyzeCsvOnlyGemini(csvContent);
      const durationMs = Math.round(performance.now() - startTime);
      const estInputTokens = Math.round(csvContent.length / 4);
      const estOutputTokens = 300;

      logAiQuery({
        queryType: 'Lista CSV',
        fileName,
        provider: 'Gemini',
        model: 'gemini-3.1-flash-lite',
        inputTokens: estInputTokens,
        outputTokens: estOutputTokens,
        durationMs,
        success: true,
      });

      return {
        data,
        metrics: {
          provider: 'Gemini',
          model: 'gemini-3.1-flash-lite',
          inputTokens: estInputTokens,
          outputTokens: estOutputTokens,
          totalTokens: estInputTokens + estOutputTokens,
          costUsd: 0,
          costCop: 0,
          formattedUsd: '$0.00',
          formattedCop: '$0 COP',
          durationMs,
          thinking: null,
        },
      };
    }
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    logAiQuery({
      queryType: 'Lista CSV',
      fileName,
      provider: config.provider === 'minimax' ? 'MiniMax' : 'Gemini',
      model: config.provider === 'minimax' ? config.minimaxModel : 'gemini-3.1-flash-lite',
      inputTokens: 0,
      outputTokens: 0,
      durationMs,
      success: false,
      error: error.message,
    });
    throw error;
  }
};

/**
 * Procesa transcripción + lista CSV con el proveedor de IA activo
 */
export const unifiedAnalyzeTranscriptionWithAttendance = async (
  transcription,
  csvContent,
  transcriptionFileName = 'transcripción',
  csvFileName = 'asistencia.csv'
) => {
  const config = getAiConfig();
  const startTime = performance.now();
  const combinedName = `${transcriptionFileName} + ${csvFileName}`;

  try {
    if (config.provider === 'minimax') {
      const res = await analyzeTranscriptionWithAttendanceMiniMax(transcription, csvContent, config);
      const durationMs = Math.round(performance.now() - startTime);
      const cost = calculateTokenCost(res.usage.inputTokens, res.usage.outputTokens, config);

      const logEntry = logAiQuery({
        queryType: 'Transcripción + CSV',
        fileName: combinedName,
        provider: 'MiniMax',
        model: config.minimaxModel,
        inputTokens: res.usage.inputTokens,
        outputTokens: res.usage.outputTokens,
        durationMs,
        success: true,
        thinkingSnippet: res.thinking ? res.thinking.substring(0, 300) : null,
      });

      return {
        data: res.data,
        metrics: {
          provider: 'MiniMax',
          model: config.minimaxModel,
          inputTokens: res.usage.inputTokens,
          outputTokens: res.usage.outputTokens,
          totalTokens: res.usage.totalTokens,
          costUsd: cost.totalUsd,
          costCop: cost.totalCop,
          formattedUsd: cost.formattedUsd,
          formattedCop: cost.formattedCop,
          durationMs,
          thinking: res.thinking,
          logId: logEntry?.id,
        },
      };
    } else {
      const data = await analyzeTranscriptionWithAttendanceGemini(transcription, csvContent);
      const durationMs = Math.round(performance.now() - startTime);
      const estInputTokens = Math.round((transcription.length + csvContent.length) / 4);
      const estOutputTokens = 800;

      logAiQuery({
        queryType: 'Transcripción + CSV',
        fileName: combinedName,
        provider: 'Gemini',
        model: 'gemini-3.1-flash-lite',
        inputTokens: estInputTokens,
        outputTokens: estOutputTokens,
        durationMs,
        success: true,
      });

      return {
        data,
        metrics: {
          provider: 'Gemini',
          model: 'gemini-3.1-flash-lite',
          inputTokens: estInputTokens,
          outputTokens: estOutputTokens,
          totalTokens: estInputTokens + estOutputTokens,
          costUsd: 0,
          costCop: 0,
          formattedUsd: '$0.00',
          formattedCop: '$0 COP',
          durationMs,
          thinking: null,
        },
      };
    }
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    logAiQuery({
      queryType: 'Transcripción + CSV',
      fileName: combinedName,
      provider: config.provider === 'minimax' ? 'MiniMax' : 'Gemini',
      model: config.provider === 'minimax' ? config.minimaxModel : 'gemini-3.1-flash-lite',
      inputTokens: 0,
      outputTokens: 0,
      durationMs,
      success: false,
      error: error.message,
    });
    throw error;
  }
};
